import React, { useCallback, useRef, useState } from 'react';
import * as Styled from './VHOverlayForCall.styled';
import FullscreenVHButton from '../../FullscreenVHButton/FullscreenVHButton';
import NarrowScreenWarningPopover from '../../VHOverlay/NarrowScreenWarningPopover/NarrowScreenWarningPopover';
import VHTimerV2 from '@vh/components/VHTimerV2/VHTimerV2';
import VHLogsAreaV2 from '@vh/components/VHLogsAreaV2/VHLogsAreaV2';
import VHInfoPreviewBox from '@vh/components/VHInfoPreviewBox/VHInfoPreviewBox';
import CallPanel from '@vh/components/VHOverlays/VHOverlayForCall/components/CallPanel/CallPanel';
import { Button } from '@virtidev/toolbox';
import VoiceFocusedAvatar from '@core/components/VirtualHumans/VoiceFocusedAvatar/VoiceFocusedAvatar';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {import('@vh/slices/chat/ChatSlice.types').ChatLogItem} ChatLogItem
 */

/**
 * @type {React.FC<{
 *  textChats: ChatLogItem[];
 *  totalTimeInSeconds: number;
 *  fullscreenElementRef: React.MutableRefObject<HTMLDivElement | null>;
 *  onTimerComplete?: () => void;
 *  title?: string;
 *  description?: string;
 *  avatarSrc?: string;
 *  onSeeMoreClick?: () => void;
 *  vhName?: string;
 *  onHangUp?: () => void;
 *  loadingUserRequest?: boolean;
 *  loadingBotResponse?: boolean;
 *  show?: React.ReactNode;
 *  logsFooter?: React.ReactNode;
 *  disabled?: boolean;
 *  muted?: boolean;
 *  onMuteChange?: (newMuted: boolean) => void;
 *  audioStream?: MediaStream;
 *  micStream?: MediaStream;
 *  isPublicPlayer?: boolean;
 *  isFullscreen?: boolean;
 * }>}
 * */
const VHOverlayForCall = ({
  textChats,
  totalTimeInSeconds,
  fullscreenElementRef,
  onTimerComplete,
  title,
  description,
  avatarSrc,
  onSeeMoreClick,
  vhName,
  onHangUp,
  loadingUserRequest,
  loadingBotResponse,
  logsFooter,
  disabled,
  muted,
  onMuteChange,
  audioStream,
  micStream,
  isPublicPlayer,
  isFullscreen,
}) => {
  const fullscreenButtonWrapperRef = useRef(null);
  const overlayRef = useRef(null);
  const [voiceMode, setVoiceMode] = useState(true);
  const handleMuteChange = useCallback(
    (muted) => {
      setVoiceMode(!muted);
      if (onMuteChange) {
        onMuteChange(muted);
      }
    },
    [onMuteChange]
  );

  const { t } = useTranslation();

  return (
    <Styled.Overlay
      ref={overlayRef}
      $expanded={voiceMode}
      $isFullscreen={isFullscreen}
    >
      <Styled.PlayerOverlay
        $isPublicPlayer={isPublicPlayer}
        $isFullscreen={isFullscreen}
      >
        <Styled.TopPanel>
          <div>
            <Button color="turquoise" onClick={onSeeMoreClick}>
              {t('brief')}
            </Button>
          </div>
          {totalTimeInSeconds > 0 && (
            <VHTimerV2
              onTimerComplete={onTimerComplete}
              totalTimeInSeconds={totalTimeInSeconds}
              disabled={disabled}
            />
          )}
          {/* <Styled.FullScreenButtonWrapper ref={fullscreenButtonWrapperRef}>
            <NarrowScreenWarningPopover
              attachToRef={fullscreenButtonWrapperRef}
              checkDimensionsRef={overlayRef}
            />
            <FullscreenVHButton fullscreenElementRef={fullscreenElementRef} />
          </Styled.FullScreenButtonWrapper> */}
        </Styled.TopPanel>
        <Styled.AvatarWrapper>
          <VoiceFocusedAvatar name={vhName} audioStream={audioStream} />
          {/* <Styled.Avatar src={avatarSrc} alt="Static Virtual Human Avatar" /> */}
        </Styled.AvatarWrapper>
        <Styled.CallPanelWrapper>
          <CallPanel
            onMuteChange={handleMuteChange}
            onHangUp={onHangUp}
            vhName={vhName}
            disabled={disabled}
            muted={muted}
            micStream={micStream}
          />
        </Styled.CallPanelWrapper>
      </Styled.PlayerOverlay>
      <Styled.SidebarWrapper $visible={voiceMode}>
        {/* <VHInfoPreviewBox
          onSeeMoreClick={onSeeMoreClick}
          title={title ?? ''}
          description={description ?? ''}
        /> */}
        <VHLogsAreaV2
          loadingBotResponse={loadingBotResponse}
          loadingUserRequest={loadingUserRequest}
          textChats={textChats}
          emptyUserTextReplacement="[audio]"
          footer={logsFooter}
        />
      </Styled.SidebarWrapper>
    </Styled.Overlay>
  );
};

export default VHOverlayForCall;
